import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, AlertCircle, X, Search } from 'lucide-react';
import AmbulanceCard from '../components/AmbulanceCard';
import DateRangeSelector from '../components/ui/DateRangeSelector';
import Pagination from '../components/ui/Pagination';
import TotalPageNumber from '../components/ui/TotalPageNumber';
import { FloatingNavBar } from '../components/Navbar';
import CameraScreen from './CameraScreen';
import NextPage from './NextPage';
import { apiService, authService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import AdminAmbulanceRegistrationModal from '../components/superadmin/AdminAmbulanceRegistrationModal';

const VehiclesPage = ({ hideAddVehicle = false, hideDateFilter = false, onViewHistory }) => {
  // Initialize fromDate to today's date (at midnight)
  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };
  
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Entries per page (controlled by dropdown)
  const [activeTab, setActiveTab] = useState('All Vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    totalVehicles: 0,
    activeRides: 0,
    availableVehicles: 0,
    offDutyDrivers: 0,
    dailyCompletedRides: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6,
    total: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { user } = useAuth();

  // Fleet Vehicle History modal: GET /v1/fleet/vehicles/:vehicle_id/history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVehicleId, setHistoryVehicleId] = useState(null);
  const [historyPlate, setHistoryPlate] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyResponse, setHistoryResponse] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const openVehicleHistory = useCallback((vehicleId, plateNumber) => {
    if (!vehicleId) return;
    setHistoryVehicleId(vehicleId);
    setHistoryPlate(plateNumber || '');
    setHistoryPage(1);
    setHistoryLimit(10);
    setHistoryResponse(null);
    setHistoryError(null);
    setHistoryOpen(true);
  }, []);

  const closeVehicleHistory = useCallback(() => {
    setHistoryOpen(false);
    setHistoryVehicleId(null);
    setHistoryPlate('');
    setHistoryResponse(null);
    setHistoryError(null);
  }, []);

  const normalizedHistory = useMemo(() => {
    const data = historyResponse?.data || historyResponse?.data?.data || null;
    if (!data) return { vehicle: null, summary: null, history: [], pagination: null };
    return {
      vehicle: data.vehicle || null,
      summary: data.summary || null,
      history: Array.isArray(data.history) ? data.history : [],
      pagination: data.pagination || null,
    };
  }, [historyResponse]);

  const fetchVehicleHistory = useCallback(async () => {
    if (!historyOpen || !historyVehicleId) return;

    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const res = await authService.fleetGetVehicleHistory(historyVehicleId, { page: historyPage, limit: historyLimit });
      setHistoryResponse(res);

      const p = res?.data?.pagination || res?.data?.data?.pagination;
      if (p && typeof p === 'object') {
        setHistoryPagination({
          page: Number(p.page || historyPage || 1),
          limit: Number(p.limit || historyLimit || 10),
          total: Number(p.total ?? 0),
          totalPages: Number(p.totalPages || 1),
        });
      } else {
        const total = Array.isArray(res?.data?.history) ? res.data.history.length : 0;
        setHistoryPagination({
          page: historyPage,
          limit: historyLimit,
          total,
          totalPages: Math.max(1, Math.ceil(total / (historyLimit || 10))),
        });
      }
    } catch (e) {
      setHistoryError(e?.message || 'Failed to load vehicle history');
      setHistoryResponse(null);
      setHistoryPagination({ page: 1, limit: historyLimit, total: 0, totalPages: 1 });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyOpen, historyVehicleId, historyPage, historyLimit]);

  useEffect(() => {
    fetchVehicleHistory();
  }, [fetchVehicleHistory]);

  // Auto-update fromDate to today's date daily
  useEffect(() => {
    const updateFromDateToToday = () => {
      const today = getTodayDate();
      
      // Use functional update to get latest fromDate state
      setFromDate(prevFromDate => {
        const currentFrom = prevFromDate ? new Date(prevFromDate) : null;
        
        // Normalize dates to compare only date part (ignore time)
        const currentFromDate = currentFrom 
          ? new Date(currentFrom.getFullYear(), currentFrom.getMonth(), currentFrom.getDate())
          : null;
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        // If fromDate is not today, update it
        if (!currentFromDate || currentFromDate.getTime() !== todayDate.getTime()) {
          return today;
        }
        return prevFromDate;
      });
    };

    // Check immediately
    updateFromDateToToday();

    // Set up interval to check every minute (to catch day changes)
    const intervalId = setInterval(updateFromDateToToday, 60000); // 60000ms = 1 minute

    return () => clearInterval(intervalId);
  }, []); // Empty dependency array - interval runs independently

  // Reset activeTab if it's set to a hidden tab when hideAddVehicle is true
  useEffect(() => {
    if (hideAddVehicle && (activeTab === 'Add Vehicle' || activeTab === 'Bulk Register')) {
      setActiveTab('All Vehicles');
    }
  }, [hideAddVehicle, activeTab]);

  // Debounce search term to prevent excessive API requests while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  // Fetch vehicles data
  const fetchVehicles = useCallback(async () => {
    if (activeTab !== 'All Vehicles') {
      return; // Only fetch when on All Vehicles tab
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const userRole = user?.role || user?.userRole;
      const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'super_admin' || userRole === 'ADMIN';
      const isHospitalUser = userRole === 'HOSPITAL_USER' || userRole === 'hospital' || userRole === 'Hospital';

      const searchParam = debouncedSearchTerm ? { search: debouncedSearchTerm } : {};
      let response;

      if (isSuperAdmin) {
        response = await apiService.getAllAmbulances({
          limit: itemsPerPage,
          page: currentPage,
          ...searchParam
        });
      } else if (isHospitalUser) {
        // Hospital dashboard: use ambulances API only, no driver endpoint, no operator_id
        response = await apiService.getAmbulances({
          limit: itemsPerPage,
          page: currentPage,
          ...searchParam
        });
      } else {
        // Fleet: try getMyVehicles first, then fallback to getAmbulances with operator_id
        try {
          response = await apiService.getMyVehicles({
            limit: itemsPerPage,
            page: currentPage,
            ...searchParam
          });
        } catch (myVehiclesError) {
          const operatorId = user?.operator?.operator_id ||
                            user?.operator_id ||
                            user?.operator?.id ||
                            user?.fleet_operator_id ||
                            user?.fleet_operator?.operator_id;
          response = await apiService.getAmbulances({
            limit: itemsPerPage,
            page: currentPage,
            operator_id: operatorId,
            ...searchParam
          });
        }
      }
      
      // Handle response format - new API returns { success, message, data: { ambulances, total, page, limit, totalPages } }
      let ambulances = [];
      let responsePagination;
      const normalizePagination = (p = {}, fallbackPage, fallbackLimit) => {
        const page = Number(p.page || fallbackPage || 1);
        const limit = Number(p.limit || fallbackLimit || itemsPerPage || 10);
        const total = Number(p.total ?? 0);
        const providedTotalPages = Number(p.totalPages || p.pages || 0);
        const totalPages = providedTotalPages > 0 ? providedTotalPages : (total > 0 ? Math.ceil(total / limit) : 1);
        return { ...p, page, limit, total, totalPages };
      };
      
      if (response.success && response.data) {
        // New API format: response.data.ambulances
        if (response.data.ambulances) {
          ambulances = response.data.ambulances;
          // Extract pagination from data object
          responsePagination = normalizePagination({
            page: response.data.page || currentPage,
            limit: response.data.limit || itemsPerPage,
            total: response.data.total || 0,
            totalPages: response.data.totalPages || 1
          }, currentPage, itemsPerPage);
        } else if (response.data.vehicles) {
          ambulances = response.data.vehicles;
        } else if (Array.isArray(response.data.data)) {
          // Common paginated format: { success, data: { data: [...], pagination: {...} } }
          ambulances = response.data.data;
          responsePagination = normalizePagination(response.data.pagination || {}, currentPage, itemsPerPage);
        } else if (Array.isArray(response.data)) {
          ambulances = response.data;
          // Common format: { success, data: [...], meta: { page, limit, total, totalPages } }
          if (response.meta && typeof response.meta === 'object') {
            responsePagination = normalizePagination(response.meta, currentPage, itemsPerPage);
          }
        }
      } else if (response.data?.ambulances) {
        // Handle nested data structure
        ambulances = response.data.ambulances;
        responsePagination = normalizePagination({
          page: response.data.page || currentPage,
          limit: response.data.limit || itemsPerPage,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 1
        }, currentPage, itemsPerPage);
      } else if (response.ambulances) {
        ambulances = response.ambulances;
      } else if (response.vehicles) {
        ambulances = response.vehicles;
      } else if (Array.isArray(response)) {
        ambulances = response;
      }

      // If API returned data array at root with meta pagination, capture it
      if (!responsePagination && response?.meta && typeof response.meta === 'object') {
        responsePagination = normalizePagination(response.meta, currentPage, itemsPerPage);
      }
      
      // Update pagination if available
      if (responsePagination) {
        const normalized = normalizePagination(responsePagination, currentPage, itemsPerPage);
        responsePagination = normalized;
        setPagination(normalized);
        // Sync currentPage with API response and ensure it's within valid range
        const apiPage = normalized.page || currentPage;
        const totalPages = normalized.totalPages || 1;
        if (apiPage !== currentPage) {
          setCurrentPage(apiPage);
        } else if (currentPage > totalPages && totalPages > 0) {
          // Reset to page 1 if current page exceeds total pages
          setCurrentPage(1);
        }
      } else if (response.pagination) {
        responsePagination = response.pagination;
        const normalized = normalizePagination(response.pagination, currentPage, itemsPerPage);
        responsePagination = normalized;
        setPagination(normalized);
        // Sync currentPage with API response and ensure it's within valid range
        const apiPage = normalized.page || currentPage;
        const totalPages = normalized.totalPages || 1;
        if (apiPage !== currentPage) {
          setCurrentPage(apiPage);
        } else if (currentPage > totalPages && totalPages > 0) {
          // Reset to page 1 if current page exceeds total pages
          setCurrentPage(1);
        }
      } else if (response.data?.pagination) {
        responsePagination = response.data.pagination;
        const normalized = normalizePagination(response.data.pagination, currentPage, itemsPerPage);
        responsePagination = normalized;
        setPagination(normalized);
        // Sync currentPage with API response and ensure it's within valid range
        const apiPage = normalized.page || currentPage;
        const totalPages = normalized.totalPages || 1;
        if (apiPage !== currentPage) {
          setCurrentPage(apiPage);
        } else if (currentPage > totalPages && totalPages > 0) {
          // Reset to page 1 if current page exceeds total pages
          setCurrentPage(1);
        }
      } else {
        // Calculate pagination from metrics
        responsePagination = normalizePagination(
          { page: currentPage, limit: itemsPerPage, total: ambulances.length, totalPages: 0 },
          currentPage,
          itemsPerPage
        );
        setPagination(responsePagination);
        // Reset to page 1 if current page exceeds total pages
        if (currentPage > responsePagination.totalPages && responsePagination.totalPages > 0) {
          setCurrentPage(1);
        }
      }
      
      // Transform API data to match AmbulanceCard format
      const transformedVehicles = ambulances.map((ambulance, index) => {
        const status = ambulance.status?.toLowerCase() || 'offline';
        const isActive = status === 'available' || status === 'active';
        const assignedDriver = ambulance.assigned_driver;
        
        // Generate card colors based on index for variety
        const cardColors = [
          { bg: 'bg-[#FDF2F8]', border: 'border-pink-200' },
          { bg: 'bg-[#FFF7ED]', border: 'border-orange-200' },
          { bg: 'bg-[#FFFBEB]', border: 'border-yellow-200' },
          { bg: 'bg-[#F0FDF4]', border: 'border-green-200' },
          { bg: 'bg-[#EFF6FF]', border: 'border-blue-200' }
        ];
        const colorIndex = index % cardColors.length;
        const cardColor = cardColors[colorIndex];
        
        const lastUpdate = ambulance.updated_at ? new Date(ambulance.updated_at) : new Date();
        const time = lastUpdate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        
        // Format location
        const location = ambulance.current_location 
          ? `${ambulance.current_location.latitude}, ${ambulance.current_location.longitude}`
          : ambulance.current_latitude && ambulance.current_longitude 
            ? `${Number(ambulance.current_latitude).toFixed(4)}, ${Number(ambulance.current_longitude).toFixed(4)}`
            : 'Unknown Location';
        
        return {
          id: ambulance.vehicle_id || ambulance.id || ambulance.plate_number,
          vehicleId: ambulance.vehicle_id || ambulance.id,
          plate_number: ambulance.plate_number,
          time: time,
          distance: ambulance.current_latitude && ambulance.current_longitude ? '4.8 km' : '0.0 km',
          duration: isActive ? '15min' : '0min',
          status: isActive ? 'Active Round Journey' : ambulance.status ? ambulance.status.charAt(0).toUpperCase() + ambulance.status.slice(1).toLowerCase() : 'Offline',
          isCritical: status === 'critical' || status === 'busy' || false,
          dashCamera: true,
          driverCamera: true,
          patientCamera: true,
          fuel: { level: 'Full Fuel', status: 'good' },
          oxygen: { level: '500 kg Oxygen', status: 'critical' },
          alcohol: { level: '200 L Alcohol', status: 'warning' },
          nitrogen: { level: '500 kg Nitrogen', status: 'good' },
          temperature: { level: '24° C Temperature', status: 'warning' },
          driverName: assignedDriver?.name || 'Unassigned',
          driverId: assignedDriver?.driver_id,
          driverPhone: assignedDriver?.phone,
          avgResponseTime: '10:30 Mins',
          rating: assignedDriver?.rating ? `${assignedDriver.rating}/5` : 'N/A',
          mapLocation: location,
          mapSubLocation: ambulance.current_address || ambulance.operator?.name || ambulance.fleet_operator?.name || 'No address',
          cardBgColor: cardColor.bg,
          cardBorderColor: cardColor.border,
          type: ambulance.type?.name || ambulance.ambulance_type?.name || ambulance.vehicle_type || (typeof ambulance.type === 'string' ? ambulance.type : null) || 'Unknown',
          operatorPhone: ambulance.operator?.contact_info || ambulance.operator?.emergency_contact || assignedDriver?.phone,
          rawData: ambulance
        };
      });
      
      setVehicles(transformedVehicles);

      // Prefer v1 meta stats when present (GET /v1/driver/my-vehicles returns meta.*),
      // else use legacy API stats (response.data.stats), else compute from current page.
      const meta = response?.meta;
      const apiStats = response?.data?.stats;
      if (meta && typeof meta === 'object') {
        setMetrics({
          totalVehicles: meta.total ?? responsePagination?.total ?? ambulances.length,
          activeRides: meta.active_rides ?? 0,
          availableVehicles: meta.available_vehicles ?? 0,
          offDutyDrivers: meta.off_duty_drivers ?? 0,
          dailyCompletedRides: meta.daily_completed_rides ?? 0
        });
      } else if (apiStats && typeof apiStats === 'object') {
        setMetrics({
          totalVehicles: responsePagination?.total ?? response?.data?.total ?? ambulances.length,
          activeRides: apiStats.active_rides ?? 0,
          availableVehicles: apiStats.available_vehicles ?? 0,
          offDutyDrivers: apiStats.off_duty_drivers ?? 0,
          dailyCompletedRides: apiStats.daily_completed_rides ?? 0
        });
      } else {
        const totalVehicles = responsePagination?.total || ambulances.length;
        const availableVehicles = ambulances.filter(v =>
          (v.status?.toLowerCase() === 'available' || v.status?.toLowerCase() === 'active')
        ).length;
        const activeRides = ambulances.filter(v =>
          v.status?.toLowerCase() === 'active' || v.status?.toLowerCase() === 'busy'
        ).length;
        const offDutyDrivers = ambulances.filter(v =>
          v.assigned_driver && (v.assigned_driver.availability_status?.toLowerCase() !== 'online' || v.assigned_driver.status?.toLowerCase() !== 'available')
        ).length;
        setMetrics({
          totalVehicles,
          activeRides,
          availableVehicles,
          offDutyDrivers,
          dailyCompletedRides: 0
        });
      }
      
    } catch (apiError) {
      console.error('Error fetching vehicles:', apiError);
      setError('Failed to load vehicles. Please try again.');
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, currentPage, itemsPerPage, activeTab, debouncedSearchTerm]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      limit,
      totalPages: prev.total ? Math.ceil(prev.total / limit) || 1 : prev.totalPages
    }));
  };

  // Render different content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'Camera Details':
        return (
          <CameraScreen 
            onBack={() => setActiveTab('All Vehicles')}
            onNext={() => setActiveTab('Map Details')}
          />
        );
      
      case 'Map Details':
        return (
          <NextPage 
            onBack={() => setActiveTab('All Vehicles')}
          />
        );
      
      case 'Add Vehicle':
        return (
          <div className="p-3 sm:p-6">
            <AdminAmbulanceRegistrationModal
              inline
              onCancel={() => setActiveTab('All Vehicles')}
              onSuccess={() => setActiveTab('All Vehicles')}
              onError={(message) => window.alert(message)}
            />
          </div>
        );

      case 'All Vehicles':
      case 'Bulk Register':
      default:
        return (
          <div className="p-6">
            {/* Error State */}
            {error && !isLoading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Data Cards (Always visible for stable layout) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-gray-900 mb-1">{metrics.totalVehicles}</div>
                <div className="text-sm text-gray-600">Total Vehicles</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-gray-900 mb-1">{metrics.activeRides}</div>
                <div className="text-sm text-gray-600">Active Rides</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-gray-900 mb-1">{metrics.availableVehicles}</div>
                <div className="text-sm text-gray-600">Available Vehicles</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-gray-900 mb-1">{metrics.offDutyDrivers}</div>
                <div className="text-sm text-gray-600">Off Duty Drivers</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-gray-900 mb-1">{metrics.dailyCompletedRides}</div>
                <div className="text-sm text-gray-600">Daily Completed Rides</div>
              </div>
            </div>

            {/* Search Bar & Pagination Controls Toolbar */}
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-5 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 max-w-xl">
                  {/* Search Bar */}
                  <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search vehicle by plate, type, driver, or fleet..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="pl-10 pr-10 h-10 w-full rounded-lg border border-gray-200 bg-gray-50/70 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                    />
                    {isLoading && debouncedSearchTerm ? (
                      <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-600" />
                    ) : searchTerm ? (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-200/60 transition-colors"
                        type="button"
                        title="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>

                  {!hideDateFilter && (
                    <div className="w-full sm:w-auto shrink-0">
                      <DateRangeSelector
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                        onRangeChange={(from, to) => {
                          setFromDate(from);
                          setToDate(to);
                          console.log('Date range changed:', from, to);
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Pagination and Total Page Number */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 shrink-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pagination 
                      currentPage={currentPage}
                      totalPages={pagination.totalPages || 1}
                      onPageChange={setCurrentPage}
                    />
                    <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                      Page {currentPage} of {pagination.totalPages || 1}
                    </span>
                  </div>
                  
                  <div className="w-full sm:w-auto">
                    <TotalPageNumber 
                      totalPages={pagination.totalPages || 1}
                      itemsPerPage={itemsPerPage}
                      onItemsPerPageChange={handleItemsPerPageChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Ambulance Cards Area */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                <span className="text-sm font-medium text-gray-600">
                  {debouncedSearchTerm ? `Searching vehicles for "${debouncedSearchTerm}"...` : 'Loading vehicles...'}
                </span>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-200 shadow-sm">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-800 font-semibold text-base mb-1">
                  {debouncedSearchTerm ? `No vehicles found matching "${debouncedSearchTerm}"` : 'No vehicles found.'}
                </p>
                <p className="text-gray-400 text-sm mb-4">
                  {debouncedSearchTerm ? 'Try checking for typos or searching by plate number, vehicle type, driver, or fleet operator.' : 'No registered vehicles are available.'}
                </p>
                {debouncedSearchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {vehicles.map((ambulance, index) => (
                  <AmbulanceCard
                    key={ambulance.id || index}
                    ambulanceId={ambulance.id}
                    vehicleId={ambulance.vehicleId || ambulance.id}
                    plateNumber={ambulance.plate_number}
                    vehicleType={ambulance.type}
                    time={ambulance.time}
                    distance={ambulance.distance}
                    duration={ambulance.duration}
                    status={ambulance.status}
                    isCritical={ambulance.isCritical}
                    dashCamera={ambulance.dashCamera}
                    driverCamera={ambulance.driverCamera}
                    patientCamera={ambulance.patientCamera}
                    fuel={ambulance.fuel}
                    oxygen={ambulance.oxygen}
                    alcohol={ambulance.alcohol}
                    nitrogen={ambulance.nitrogen}
                    temperature={ambulance.temperature}
                    driverName={ambulance.driverName}
                    avgResponseTime={ambulance.avgResponseTime}
                    rating={ambulance.rating}
                    mapLocation={ambulance.mapLocation}
                    mapSubLocation={ambulance.mapSubLocation}
                    cardBgColor={ambulance.cardBgColor}
                    cardBorderColor={ambulance.cardBorderColor}
                    onCallDriver={() => {
                      // Prefer assigned driver's phone if present (fleet expectation)
                      const phone =
                        ambulance.rawData?.assigned_driver?.phone ||
                        ambulance.driverPhone ||
                        ambulance.rawData?.operator?.contact_info ||
                        ambulance.operatorPhone;
                      if (!phone) {
                        console.log(`No phone number available for ${ambulance.driverName}`);
                        return;
                      }
                      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(phone).catch((err) => {
                          console.error('Failed to copy phone number:', err);
                        });
                      }
                    }}
                    onViewHistory={onViewHistory
                      ? () => onViewHistory(ambulance.vehicleId || ambulance.id, ambulance.plate_number)
                      : () => openVehicleHistory(ambulance.vehicleId || ambulance.id, ambulance.plate_number)}
                    showViewProfile={false}
                    onViewMore={() => {
                      setActiveTab('Camera Details');
                    }}
                    onMapClick={() => {
                      if (ambulance.rawData?.current_location) {
                        const { latitude, longitude } = ambulance.rawData.current_location;
                        window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
                      } else if (ambulance.mapLocation && ambulance.mapLocation !== 'Unknown Location') {
                        window.open(`https://www.google.com/maps?q=${ambulance.mapLocation}`, '_blank');
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen">
      {/* Render content based on active tab */}
      {renderContent()}

      {/* Vehicle History Modal */}
      <Dialog open={historyOpen} onOpenChange={(open) => (open ? setHistoryOpen(true) : closeVehicleHistory())}>
        <DialogContent className="max-w-4xl p-0">
          <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b">
            <div className="min-w-0">
              <DialogTitle className="text-base sm:text-lg font-semibold">
                Vehicle History{historyPlate ? ` — ${historyPlate}` : ''}
              </DialogTitle>
              {normalizedHistory?.vehicle?.assigned_driver?.name && (
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                  Driver: {normalizedHistory.vehicle.assigned_driver.name}
                  {normalizedHistory.vehicle.assigned_driver.phone ? ` • ${normalizedHistory.vehicle.assigned_driver.phone}` : ''}
                </p>
              )}
            </div>
            <button
              className="p-2 rounded hover:bg-gray-100"
              onClick={closeVehicleHistory}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </DialogHeader>

          <div className="px-6 py-4">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span className="text-gray-600">Loading vehicle history...</span>
              </div>
            ) : historyError ? (
              <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
                <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">{historyError}</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                {normalizedHistory.summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <div className="bg-white rounded-lg border p-3">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-xl font-semibold text-gray-900">{normalizedHistory.summary.total_bookings ?? 0}</p>
                    </div>
                    <div className="bg-white rounded-lg border p-3">
                      <p className="text-xs text-gray-500">Completed</p>
                      <p className="text-xl font-semibold text-green-600">{normalizedHistory.summary.completed_bookings ?? 0}</p>
                    </div>
                    <div className="bg-white rounded-lg border p-3">
                      <p className="text-xs text-gray-500">Active</p>
                      <p className="text-xl font-semibold text-blue-600">{normalizedHistory.summary.active_bookings ?? 0}</p>
                    </div>
                    <div className="bg-white rounded-lg border p-3">
                      <p className="text-xs text-gray-500">Cancelled</p>
                      <p className="text-xl font-semibold text-red-600">{normalizedHistory.summary.cancelled_bookings ?? 0}</p>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                <div className="mb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pagination
                        currentPage={historyPage}
                        totalPages={historyPagination.totalPages || 1}
                        onPageChange={setHistoryPage}
                      />
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        Page {historyPage} of {historyPagination.totalPages || 1}
                      </span>
                    </div>
                    <div className="w-full sm:w-auto">
                      <TotalPageNumber
                        totalPages={historyPagination.totalPages || 1}
                        itemsPerPage={historyLimit}
                        onItemsPerPageChange={(n) => { setHistoryLimit(n); setHistoryPage(1); }}
                      />
                    </div>
                  </div>
                </div>

                {/* History table */}
                {normalizedHistory.history.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600">No history found.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left font-semibold px-4 py-3">Booking ID</th>
                          <th className="text-left font-semibold px-4 py-3">Status</th>
                          <th className="text-left font-semibold px-4 py-3">Patient</th>
                          <th className="text-left font-semibold px-4 py-3">Type</th>
                          <th className="text-left font-semibold px-4 py-3">Fare</th>
                          <th className="text-left font-semibold px-4 py-3">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalizedHistory.history.map((h) => (
                          <tr key={h.booking_id} className="border-t">
                            <td className="px-4 py-3 font-mono text-gray-900">{h.booking_id}</td>
                            <td className="px-4 py-3 text-gray-700">{h.status}</td>
                            <td className="px-4 py-3 text-gray-700">{h.patient_name || '—'}</td>
                            <td className="px-4 py-3 text-gray-700">{h.booking_type || '—'}</td>
                            <td className="px-4 py-3 text-gray-700">{h.fare != null ? `₹${h.fare}` : '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{h.created_at ? new Date(h.created_at).toLocaleString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Navigation Bar - Always visible */}
      <FloatingNavBar
        tabs={
          hideAddVehicle
            ? ['All Vehicles', 'Camera Details', 'Map Details']
            : ['All Vehicles', 'Add Vehicle', 'Bulk Register', 'Camera Details', 'Map Details']
        }
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showLogout={false}
        className="mt-8"
      />
    </div>
  );
};

export default VehiclesPage;
